import type {LegalDocumentResponse, SearchLegalDocumentsRequest} from '@getmoss/api-gateway'
import type {LocalesType} from 'src/common/constants'
import type {DocumentTypeFilterValue, FilterOption, MarketFilterValue, ProductFilterValue} from '../constants'
import {LANGUAGE_LABELS, TRANSLATIONS} from '../constants'

/**
 * Generates product filter options based on locale
 */
export const getProductFilterOptions = (locale: LocalesType): FilterOption<ProductFilterValue | undefined>[] => [
  {category_name: TRANSLATIONS.ALL_PRODUCTS[locale], value: undefined},
  {category_name: TRANSLATIONS.PLATFORM[locale], value: 'PLATFORM' as const},
  {category_name: TRANSLATIONS.CREDIT[locale], value: 'CREDIT' as const},
  {category_name: TRANSLATIONS.CARDS[locale], value: 'CARDS' as const},
  {category_name: TRANSLATIONS.WALLET[locale], value: 'WALLET' as const},
  {category_name: TRANSLATIONS.PAY_WITH_MOSS[locale], value: 'PAY_WITH_MOSS' as const},
]

/**
 * Generates market filter options based on locale
 */
export const getMarketFilterOptions = (locale: LocalesType): FilterOption<MarketFilterValue | undefined>[] => [
  {category_name: TRANSLATIONS.ALL_MARKETS[locale], value: undefined},
  {category_name: TRANSLATIONS.GERMANY[locale], value: 'DE'},
  {category_name: TRANSLATIONS.UNITED_KINGDOM[locale], value: 'GB'},
  {category_name: TRANSLATIONS.NETHERLANDS[locale], value: 'NL'},
  {category_name: TRANSLATIONS.OTHER_EEA_COUNTRIES[locale], value: 'OTHER'},
]

/**
 * Generates document type filter options based on locale
 */
export const getDocumentTypeFilterOptions = (
  locale: LocalesType,
): FilterOption<DocumentTypeFilterValue | undefined>[] => [
  {category_name: TRANSLATIONS.ALL_DOCUMENT_TYPES[locale], value: undefined},
  {category_name: TRANSLATIONS.TNC[locale], value: 'TNC' as const},
  {category_name: TRANSLATIONS.PRIVACY_POLICY[locale], value: 'PRIVACY_POLICY' as const},
  {category_name: TRANSLATIONS.DATA_PROCESSING_AGREEMENT[locale], value: 'DATA_PROCESSING_AGREEMENT' as const},
  {category_name: TRANSLATIONS.FEE_SCHEDULE[locale], value: 'FEE_SCHEDULE' as const},
]

/**
 * Builds search request parameters from selected filters
 */
export const buildSearchRequest = (
  productValues: (ProductFilterValue | undefined)[] | ProductFilterValue | undefined,
  marketValue: MarketFilterValue | undefined,
  documentTypeValue: DocumentTypeFilterValue | undefined,
): SearchLegalDocumentsRequest => {
  let products: SearchLegalDocumentsRequest['products']

  if (Array.isArray(productValues)) {
    const filteredValues = productValues.filter((v) => v !== undefined)
    products = filteredValues.length > 0 ? (filteredValues as SearchLegalDocumentsRequest['products']) : undefined
  } else {
    products = productValues ? ([productValues] as SearchLegalDocumentsRequest['products']) : undefined
  }

  const markets = marketValue ? ([marketValue] as SearchLegalDocumentsRequest['markets']) : undefined

  const documentTypes = documentTypeValue
    ? ([documentTypeValue] as SearchLegalDocumentsRequest['documentTypes'])
    : undefined

  return {products, markets, documentTypes}
}

/**
 * Gets the URL for a document in a specific language, with fallbacks
 */
export const getDocumentUrl = (docData: LegalDocumentResponse, lang: string): string => {
  if (docData.url && docData.url[lang]) {
    return docData.url[lang]
  }

  if (docData.url && docData.url.en) {
    return docData.url.en
  }

  if (docData.url) {
    const firstUrl = Object.values(docData.url)[0]

    if (firstUrl) {
      return firstUrl
    }
  }

  return '#'
}

/**
 * Gets the description for a document in a specific language, with fallbacks
 */
export const getDocumentDescription = (docData: LegalDocumentResponse, lang: string, locale: LocalesType): string => {
  if (docData.description && docData.description[lang]) {
    return docData.description[lang]
  }

  if (docData.description && docData.description.en) {
    return docData.description.en
  }

  return getCategoryLabel(docData.documentType, locale)
}

/**
 * Formats a document category for display
 */
export const getCategoryLabel = (category: string | undefined, locale: LocalesType): string => {
  if (!category) {
    return TRANSLATIONS.DOCUMENT[locale]
  }

  // Check if translation exists for this document type
  if (category in TRANSLATIONS) {
    return TRANSLATIONS[category as keyof typeof TRANSLATIONS][locale]
  }

  // Fallback to formatted category name
  return category.replace(/_/g, ' ')
}

/**
 * Gets available languages for a document based on URL and description availability
 */
export const getAvailableLanguages = (
  doc: LegalDocumentResponse,
  locale: LocalesType,
): Array<{code: string; label: string; flag: string}> => {
  const languages = []

  if (doc.url?.en) {
    languages.push({code: 'en', label: LANGUAGE_LABELS.en[locale], flag: '🇬🇧'})
  }

  if (doc.url?.de) {
    languages.push({code: 'de', label: LANGUAGE_LABELS.de[locale], flag: '🇩🇪'})
  }

  if (doc.url?.nl) {
    languages.push({code: 'nl', label: LANGUAGE_LABELS.nl[locale], flag: '🇳🇱'})
  }

  return languages
}

/**
 * Converts router locale format (e.g., 'de-de', 'nl-nl', 'en-us') to document language code (e.g., 'de', 'nl', 'en')
 */
const getLanguageCodeFromLocale = (locale: LocalesType): string =>
  // Extract the first part before the hyphen (e.g., 'de-de' -> 'de', 'nl-nl' -> 'nl')
  locale.split('-')[0]

/**
 * Gets the default language for a document based on website locale
 */
export const getDefaultLanguage = (
  availableLanguages: Array<{code: string; label: string; flag: string}>,
  locale: LocalesType,
): string => {
  const preferredLang = getLanguageCodeFromLocale(locale)
  const langExists = availableLanguages.find((lang) => lang.code === preferredLang)

  return langExists ? preferredLang : availableLanguages[0].code
}

/**
 * Map of supplier values to their display labels
 */
const SUPPLIER_LABEL_MAP: Record<string, string> = {
  NUFIN_GMBH: 'Nufin GmbH',
  MOSS_GMBH: 'Moss GmbH',
  GETMOSS_UK_LTD: 'GetMoss UK Ltd.',
  GETMOSS_NL_BV: 'GetMoss NL B.V.',
  AIRWALLEX_NETHERLANDS_BV: 'Airwallex (Netherlands) B.V.',
  AIRWALLEX_UK_LIMITED: 'Airwallex (UK) Limited',
  TRANSACT_PAYMENTS_LIMITED: 'Transact Payments Limited',
}

/**
 * Formats a supplier name for display
 */
export const getSupplierLabel = (supplier: string | undefined): string => {
  if (!supplier) {
    return '-'
  }

  return SUPPLIER_LABEL_MAP[supplier] || supplier
}
